import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Copy, Check, Download, AlertTriangle, Loader2, Users } from "lucide-react";
import { CensusUpload } from "./CensusUpload";
import { EmployeeList } from "./EmployeeList";
import { EmployeeModal } from "./EmployeeModal";
import { ConfirmModal } from "./ConfirmModal";
import { useFormStore } from "../store/formStore";
import { getApi } from "../api";
import { trackWarning } from "../telemetry/appInsights";
import { saveEmployees } from "../services/saveDraft";
import { MAX_EMPLOYEES } from "../validation/censusValidation";
import type { Employee } from "../types/form";
import type { useStepValidation } from "../hooks";
import { getConfig } from "../config";

interface EmployeeListingSectionProps {
  validation: ReturnType<typeof useStepValidation>;
}

/**
 * Step 7b — employee listing with census upload, manual add/edit/delete,
 * IHQ enrollment-link copy, and optimistic-save rollback. Rendered after
 * the document-upload sections of StepDocuments.
 */
export function EmployeeListingSection({ validation }: EmployeeListingSectionProps) {
  const censusTemplateUrl = `${getConfig().ASSETS_BASE_URL}/CensusTemplateForAttestation.xlsx`;
  const claimsCensusTemplateUrl = `${getConfig().ASSETS_BASE_URL}/Final Enrollment Census File.xlsx`;
  const employees = useFormStore((s) => s.data.employees);
  const censusFile = useFormStore((s) => s.data.documents.censusFile);
  const addEmployee = useFormStore((s) => s.addEmployee);
  const updateEmployee = useFormStore((s) => s.updateEmployee);
  const removeEmployee = useFormStore((s) => s.removeEmployee);
  const setEmployees = useFormStore((s) => s.setEmployees);
  const setDocument = useFormStore((s) => s.setDocument);

  const uwMethod = useFormStore((s) => s.meta.uwMethod) ?? "";
  const availablePlans = useFormStore((s) => s.meta.availablePlans);
  // Derived from the user's Coverage-page answer (data.offeringDental / data.offeringVision).
  // null (not yet answered) → default to showing the dropdown.
  const offersDental = useFormStore((s) => s.data.offeringDental !== "No");
  const offersVision = useFormStore((s) => s.data.offeringVision !== "No");
  const ihqLink = useFormStore((s) => s.meta.ihqLink);

  const [modalState, setModalState] = useState<
    | { type: "add" }
    | { type: "edit"; index: number; employee: Employee }
    | null
  >(null);
  const [confirmState, setConfirmState] = useState<
    | { type: "delete"; index: number; employee: Employee }
    | { type: "removeFile" }
    | { type: "overwrite"; file: File; employees: Employee[] }
    | null
  >(null);
  const [copied, setCopied] = useState(false);
  const [savingEmployees, setSavingEmployees] = useState(false);
  const [removingCensus, setRemovingCensus] = useState(false);
  const censusParseOptions = useMemo(() => ({ offersDental, offersVision, availablePlans }), [offersDental, offersVision, availablePlans]);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(copyTimer.current), []);
  // Generation token for in-flight census uploads. Each upload captures the value
  // at start; if the user Removes (or kicks off another upload) before it
  // resolves, we bump the token and the stale upload's result is discarded so it
  // can't resurrect a removed file or stomp on a newer upload.
  const censusUploadGen = useRef(0);

  // Lock list actions when any list-mutating modal/confirm is open or a save is
  // in flight — otherwise the user can delete or open another modal under a
  // confirm dialog and corrupt the index referenced by the open confirm.
  const actionsLocked = savingEmployees || removingCensus
    || confirmState?.type === "overwrite" || confirmState?.type === "removeFile";

  const handleCopyIhqLink = useCallback(() => {
    if (!ihqLink) return;
    void navigator.clipboard.writeText(ihqLink);
    setCopied(true);
    clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 2000);
  }, [ihqLink]);

  const doUploadAndReplace = useCallback(async (parsed: Employee[], file: File) => {
    const gen = ++censusUploadGen.current;
    const previousEmployees = useFormStore.getState().data.employees;
    setEmployees(parsed);
    // Show the file immediately so the upload button is replaced right away
    setDocument("censusFile", { fileId: "", name: file.name, size: file.size });
    setSavingEmployees(true);
    try {
      const formId = useFormStore.getState().formId;
      await Promise.all([
        formId
          ? getApi().uploadFile(formId, "censusFile", file).then((result) => {
              // Skip if a newer upload/remove has been issued — otherwise we'd
              // either resurrect a removed file or overwrite a newer upload's ref.
              if (censusUploadGen.current === gen) setDocument("censusFile", result);
            })
          : Promise.resolve(),
        saveEmployees(),
      ]);
    } catch {
      if (censusUploadGen.current !== gen) return;
      trackWarning("CensusUpload: census upload/save failed", { docType: "censusFile" });
      setEmployees(previousEmployees);
      setDocument("censusFile", null);
    } finally {
      if (censusUploadGen.current === gen) setSavingEmployees(false);
    }
  }, [setEmployees, setDocument]);

  const handleCensusParsed = useCallback((parsed: Employee[], file: File) => {
    const currentEmployees = useFormStore.getState().data.employees;
    if (currentEmployees.length > 0) {
      setConfirmState({ type: "overwrite", file, employees: parsed });
      return;
    }
    void doUploadAndReplace(parsed, file);
  }, [doUploadAndReplace]);

  const handleRemoveCensusFile = useCallback(async () => {
    // Invalidate any in-flight upload so its onUpload result can't re-apply
    // the file after we've cleared it.
    censusUploadGen.current++;
    setConfirmState(null);
    setRemovingCensus(true);
    try {
      if (censusFile?.fileId) {
        const api = getApi();
        await api.deleteFile(censusFile.fileId);
      }
    } catch {
      trackWarning("CensusUpload: file delete failed", { fileId: censusFile?.fileId ?? "" });
    }
    setDocument("censusFile", null);
    setEmployees([]);
    try { await saveEmployees(); } catch { /* saveEmployees logs internally */ }
    setRemovingCensus(false);
  }, [censusFile, setDocument, setEmployees]);

  const handleSaveEmployee = useCallback(async (employee: Employee) => {
    const previousEmployees = useFormStore.getState().data.employees;
    if (modalState?.type === "edit") {
      updateEmployee(modalState.index, employee);
    } else {
      addEmployee(employee);
    }
    setModalState(null);
    setSavingEmployees(true);
    try {
      const ok = await saveEmployees();
      // Roll back optimistic update so UI doesn't display rows that aren't on the server.
      // saveEmployees publishes its own error event — we only need to revert state here.
      if (!ok) setEmployees(previousEmployees);
    } finally { setSavingEmployees(false); }
  }, [modalState, addEmployee, updateEmployee, setEmployees]);

  const handleSaveAndAdd = useCallback(async (employee: Employee) => {
    const previousEmployees = useFormStore.getState().data.employees;
    addEmployee(employee);
    setSavingEmployees(true);
    try {
      const ok = await saveEmployees();
      if (!ok) setEmployees(previousEmployees);
    } finally { setSavingEmployees(false); }
  }, [addEmployee, setEmployees]);

  const handleDeleteConfirm = useCallback(async () => {
    if (confirmState?.type === "delete") {
      const previousEmployees = useFormStore.getState().data.employees;
      removeEmployee(confirmState.index);
      setConfirmState(null);
      setSavingEmployees(true);
      try {
        const ok = await saveEmployees();
        if (!ok) setEmployees(previousEmployees);
      } finally { setSavingEmployees(false); }
    } else {
      setConfirmState(null);
    }
  }, [confirmState, removeEmployee, setEmployees]);

  // Claims-based quotes (UW_Method__c = Claims) use a different census template
  // ("Final Enrollment Census File") and a validate-and-attach upload — the file
  // is never parsed into employee records and there's no manual employee list.
  if (uwMethod === "Claims") {
    const censusError = validation.getFieldError("documents.censusFile");
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-brand-navy mb-4 flex items-center gap-2">
          <Users size={20} className="text-gray-500" aria-hidden="true" />
          Employee Listing
        </h2>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-blue-800">
            An eligibility census must be submitted to Optimyl. Complete the Final Enrollment
            Census File (linked below). Note, there is an example tab that shows the correct
            formatting. Please include all employees on Cobra and in the Waiting Period and
            include their Enrollment Status in the required field.
          </p>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <a
              href={claimsCensusTemplateUrl}
              download
              className="flex items-center gap-1.5 text-sm font-medium text-brand-green hover:underline"
            >
              <Download size={16} className="shrink-0" />
              Download Employee Census Template
            </a>
            <span className="text-xs text-gray-500">(This template must be used.)</span>
          </div>
        </div>

        {/* Upload — overlay spinner while saving/removing, same as the standard listing */}
        <div className="relative">
          {(savingEmployees || removingCensus) && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 rounded-lg" role="status">
              <Loader2 size={28} className="text-brand-green animate-spin" />
              <span className="sr-only">Saving...</span>
            </div>
          )}

          {/* ADO-3484: explicit required-asterisk label so users can tell the census file is
              mandatory in Claims mode before they hit Submit and see a validation error. */}
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Employee Census File
            <span className="text-danger" aria-hidden="true">&nbsp;*</span>
          </label>

          <CensusUpload
            censusFile={censusFile}
            removing={removingCensus}
            claims
            onParsed={handleCensusParsed}
            onRemove={() => setConfirmState({ type: "removeFile" })}
          />

          {censusError && !censusFile && (
            <p className="mt-2 text-sm text-danger" role="alert">{censusError}</p>
          )}
        </div>

        {confirmState?.type === "removeFile" && (
          <ConfirmModal
            title="Remove Census File?"
            message="Are you sure you want to remove this census file?"
            confirmLabel="Remove"
            cancelLabel="Keep File"
            onConfirm={() => void handleRemoveCensusFile()}
            onCancel={() => setConfirmState(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
      <h2 className="text-lg font-semibold text-brand-navy mb-4">Employee Listing</h2>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="text-sm text-blue-800 space-y-3">
          <p>
            <strong>Any employee in their Cobra Election Period or former employees who are
            covered under COBRA</strong> on your existing health plan <strong>must complete an Optimyl
            Health Coverage and Enrollment form</strong> to receive coverage through the
            Optimyl Benefits Group Plan.
          </p>
          <p>
            <strong>Any Full Time Eligible employee hired before the policy effective date or
            issuance date</strong>, whichever is later, <strong>must be disclosed and complete a Optimyl
            Health Coverage and Enrollment form</strong> for coverage or complete the waiver
            section of the Enrollment form if declining coverage.
          </p>
          <p>
            If you have employees or Cobra participants that still need to enroll, you
            can send the below Optimyl Health Coverage and Enrollment form link directly
            to them. If you are collecting enrollments through a third-party platform or
            paper, you or your broker must notify Optimyl so we can confirm receipt before
            your policy is issued. Note, any changes in enrollment may cause a change in rates.
          </p>

          {ihqLink && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mt-2 bg-gray-100 border border-gray-200 rounded-lg p-3">
              <button
                onClick={handleCopyIhqLink}
                className={`flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md cursor-pointer transition-colors shrink-0 min-w-[130px] ${
                  copied
                    ? "text-brand-green border border-brand-green bg-brand-green-05"
                    : "text-gray-600 border border-gray-400 bg-white hover:bg-gray-50"
                }`}
              >
                {copied ? <Check size={16} className="shrink-0" /> : <Copy size={16} className="shrink-0" />}
                <span className="whitespace-nowrap">{copied ? "Copied!" : "Copy IHQ Link"}</span>
              </button>
              <a href={ihqLink} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 break-all sm:truncate hover:underline">{ihqLink}</a>
              {/* Screen-reader announcement for copy feedback — the visual button label
                  change alone isn't announced by AT. aria-live="polite" reads "Copied!"
                  when the state flips. */}
              <span className="sr-only" aria-live="polite" role="status">
                {copied ? "IHQ link copied to clipboard" : ""}
              </span>
            </div>
          )}

          <p>List all eligible employees, including COBRA and waiting period individuals.</p>
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <a
            href={censusTemplateUrl}
            download
            className="flex items-center gap-1.5 text-sm font-medium text-brand-green hover:underline"
          >
            <Download size={16} className="shrink-0" />
            Download Employee Census Template
          </a>
          <span className="text-xs text-gray-500">(If opting to upload, this template must be used.)</span>
        </div>
      </div>

      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
        <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800">
          <span className="font-semibold">DO NOT</span> list non-eligible employees (e.g., part time employees).
        </p>
      </div>

      {/* Census + employee list — overlay spinner while saving */}
      <div className="relative">
        {(savingEmployees || removingCensus) && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 rounded-lg" role="status">
            <Loader2 size={28} className="text-brand-green animate-spin" />
            <span className="sr-only">Saving...</span>
          </div>
        )}

        <CensusUpload
          censusFile={censusFile}
          removing={removingCensus}
          parseOptions={censusParseOptions}
          onParsed={handleCensusParsed}
          onRemove={() => setConfirmState({ type: "removeFile" })}
        />

        <div className="flex items-center gap-4 my-4">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-xs text-gray-500 whitespace-nowrap">or add employees manually</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        <EmployeeList
          employees={employees}
          offersDental={offersDental}
          offersVision={offersVision}
          availablePlans={availablePlans}
          showErrors={!!validation.getFieldError("employees")}
          errorMessage={validation.getFieldError("employees")}
          onAdd={!actionsLocked && employees.length < MAX_EMPLOYEES ? () => setModalState({ type: "add" }) : undefined}
          onEdit={actionsLocked ? () => {} : (index) => setModalState({ type: "edit", index, employee: employees[index] })}
          onDelete={actionsLocked ? () => {} : (index) => setConfirmState({ type: "delete", index, employee: employees[index] })}
          actionsDisabled={actionsLocked}
        />
      </div>


      {modalState && (
        <EmployeeModal
          mode={modalState.type === "edit" ? "edit" : "add"}
          initial={modalState.type === "edit" ? modalState.employee : undefined}
          availablePlans={availablePlans}
          offersDental={offersDental}
          offersVision={offersVision}
          onSave={handleSaveEmployee}
          onSaveAndAdd={modalState.type === "add" && employees.length < MAX_EMPLOYEES - 1 ? handleSaveAndAdd : undefined}
          onClose={() => setModalState(null)}
        />
      )}

      {confirmState?.type === "delete" && (
        <ConfirmModal
          title="Delete Employee Record?"
          message={`Are you sure you want to delete ${confirmState.employee.lastName}, ${confirmState.employee.firstName}? This cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={handleDeleteConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}

      {confirmState?.type === "removeFile" && (
        <ConfirmModal
          title="Remove Census File?"
          message={`Removing this file will delete all ${employees.length} employee records from the list below. This cannot be undone.`}
          confirmLabel="Remove & Delete Records"
          cancelLabel="Keep File"
          onConfirm={() => void handleRemoveCensusFile()}
          onCancel={() => setConfirmState(null)}
        />
      )}

      {confirmState?.type === "overwrite" && (
        <ConfirmModal
          title="Overwrite Existing Records?"
          message={<>You have <strong>{employees.length} existing record{employees.length !== 1 ? "s" : ""}</strong> in the list. Uploading this file will overwrite all of them with the records from <strong>{confirmState.file.name}</strong>.</>}
          confirmLabel="Overwrite Records"
          confirmVariant="danger"
          onConfirm={() => {
            void doUploadAndReplace(confirmState.employees, confirmState.file);
            setConfirmState(null);
          }}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </div>
  );
}
